import React, { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", company: "", country: "", email: "", phone: "",
  destination_port: "", incoterm: "FOB", packaging: "", delivery_timeline: "", message: "", website: "",
};

const QuoteModal = ({ open, onOpenChange, product, source = "product-modal" }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.country || !form.email || !form.phone) {
      toast.error("Please fill all required fields");
      return;
    }
    setBusy(true);
    try {
      const items = product
        ? [{ product_id: product.id, product_name: product.name, category: product.category, packaging: form.packaging, incoterm: form.incoterm, destination_port: form.destination_port }]
        : [];
      const { data } = await axios.post(`${API}/enquiries`, { ...form, items, source, page: window.location.pathname });
      toast.success("Enquiry sent — we will contact you shortly");
      setForm(empty);
      onOpenChange(false);
      window.location.href = `/enquiry-confirmed/${data.id}`;
    } catch (err) {
      toast.error("Could not submit. Please try again or WhatsApp us.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="quote-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#004225]">Request a Bulk Quote {product ? `— ${product.name}` : ""}</DialogTitle>
          <DialogDescription>Share your requirement — our trade desk will respond with specifications and commercials.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={on("name")} data-testid="quote-name" required /></div>
          <div><Label>Company *</Label><Input value={form.company} onChange={on("company")} data-testid="quote-company" required /></div>
          <div><Label>Country *</Label><Input value={form.country} onChange={on("country")} data-testid="quote-country" required /></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={on("email")} data-testid="quote-email" required /></div>
          <div><Label>Phone / WhatsApp *</Label><Input value={form.phone} onChange={on("phone")} data-testid="quote-phone" required /></div>
          <div><Label>Destination Port</Label><Input value={form.destination_port} onChange={on("destination_port")} data-testid="quote-port" /></div>
          <div>
            <Label>Incoterm</Label>
            <Select value={form.incoterm} onValueChange={on("incoterm")}>
              <SelectTrigger data-testid="quote-incoterm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FOB">FOB</SelectItem>
                <SelectItem value="CFR">CFR</SelectItem>
                <SelectItem value="CIF">CIF</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Packaging</Label><Input placeholder="e.g. 25 kg PP" value={form.packaging} onChange={on("packaging")} data-testid="quote-packaging" /></div>
          <div className="sm:col-span-2"><Label>Message / Requirement</Label><Textarea rows={3} value={form.message} onChange={on("message")} data-testid="quote-message" placeholder="Quantity, packaging preferences, delivery timeline…" /></div>
          <input type="text" name="website" value={form.website} onChange={on("website")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0 }} aria-hidden="true" />
          <DialogFooter className="sm:col-span-2">
            <Button type="submit" disabled={busy} data-testid="quote-submit" className="btn-primary rounded-full px-6 uppercase tracking-widest text-xs">
              {busy ? "Sending…" : "Send Enquiry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
